'use client';

import { useCallback, useRef, useState } from 'react';

const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.85;
const MAX_FILES = 10;

export interface UseImageImportReturn {
  importImages: () => void;
  isImporting: boolean;
  importError: string | null;
}

export function useImageImport(
  onImagesReady: (base64Images: string[]) => void,
): UseImageImportReturn {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const importImages = useCallback(() => {
    if (isImporting) return;

    const existing = inputRef.current;
    if (existing) {
      existing.remove();
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    input.multiple = true;

    input.addEventListener('change', async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) {
        input.remove();
        return;
      }

      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length === 0) {
        setImportError('Please select image files');
        input.remove();
        return;
      }

      setIsImporting(true);
      setImportError(null);

      try {
        const images: string[] = [];
        const toProcess = imageFiles.slice(0, MAX_FILES);

        for (const file of toProcess) {
          const dataUrl = await readFileAsDataUrl(file);
          const processed = await downscaleImage(dataUrl);
          images.push(processed);
        }

        if (images.length > 0) {
          onImagesReady(images);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import images';
        setImportError(message);
      } finally {
        setIsImporting(false);
        input.remove();
      }
    });

    inputRef.current = input;
    document.body.appendChild(input);
    input.click();
  }, [isImporting, onImagesReady]);

  return {
    importImages,
    isImporting,
    importError,
  };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function downscaleImage(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const result = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      canvas.width = 0;
      canvas.height = 0;
      resolve(result);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}
